/**
 * A DeCA's sendings by email (#219), as the «Envíos por correo» panel reads them. Pure: no React.
 *
 * The four states the plan names, and a fifth, from each letter's own status (`DecaEmailRecipient`):
 *  - «Pendiente» — still ours: queued, or being handed to SES;
 *  - «Enviado» — SES took it; the receiving server has not answered yet, or asked to wait;
 *  - «Entregado» — the receiving server took it;
 *  - «Rechazado» — it did not arrive, or will not: with its reason;
 *  - «No enviado» — we never wrote it, the address being dead: an earlier letter to it bounced or never
 *    arrived, or its domain takes no mail.
 *    Its own state since Aleksandr's check of 2026-10-23: under «Rechazado» it read as a fresh refusal
 *    by the receiving server, which never saw it.
 */

import type { Deca, DecaEmailRecipient, DecaEmailSending } from '@deca/api'

export type RecipientState = 'PENDING' | 'SENT' | 'DELIVERED' | 'REFUSED' | 'NOT_SENT' | 'UNKNOWN'

export function recipientState(recipient: Pick<DecaEmailRecipient, 'status'>): RecipientState {
  switch (recipient.status) {
    case 'QUEUED':
    case 'SENDING':
      return 'PENDING'
    case 'SENT':
    case 'DELAYED':
      return 'SENT'
    case 'DELIVERED':
      return 'DELIVERED'
    case 'BOUNCED':
    case 'REJECTED':
    case 'COMPLAINED':
    case 'FAILED':
      return 'REFUSED'
    case 'SKIPPED':
      return 'NOT_SENT'
    // Pruned after 90 days, as every letter: the sending is kept, its letters' fates are not.
    default:
      return 'UNKNOWN'
  }
}

/** How often the panel reads again: every few seconds while a letter is ours or on its way. */
export const DECA_EMAIL_POLL_MS = 4000

/** A sending older than this is not watched any more: SES answers within seconds or hours. */
export const WATCH_SENDING_MS = 10 * 60_000

export function decaEmailPollInterval(sendings: readonly DecaEmailSending[], now: number): number | false {
  const watched = sendings.some(
    (sending) =>
      now - new Date(sending.createdAt).getTime() < WATCH_SENDING_MS &&
      sending.recipients.some((recipient) => {
        const state = recipientState(recipient)
        return state === 'PENDING' || state === 'SENT'
      }),
  )
  return watched ? DECA_EMAIL_POLL_MS : false
}

/** «Modificado desde el último envío»: the document has a version the latest sending did not carry. */
export function modifiedSinceLastEmail(deca: { versionNo: number; lastEmail?: { versionNo: number } | null }): boolean {
  return Boolean(deca.lastEmail) && deca.versionNo > (deca.lastEmail as { versionNo: number }).versionNo
}

/**
 * Whether «Enviar por correo» may be offered: a document whose link serves — generated, neither
 * voided nor expired, and with its link open (`directDownloadUrl` is null once it has closed). The
 * server's `DECA_NOT_SENDABLE` decides.
 */
export function canSendByEmail(deca: Pick<Deca, 'estado' | 'directDownloadUrl'>): boolean {
  return (deca.estado === 'EN CURSO' || deca.estado === 'MODIFICADO' || deca.estado === 'FINALIZADO') && Boolean(deca.directDownloadUrl)
}
